/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 */
import cx from 'classnames';
import * as React from 'react';

import type { TagInputProps } from '../../types';
import Icon from '../Icon/Icon';
import './TagInput.less';

const TagInput = React.forwardRef<HTMLDivElement, TagInputProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      disabled = false,
      placeholder,
      addOnEnter = true,
      size = 'medium',
      invalid = false,
      icon,
      id,
      name,
      'aria-label': ariaLabel,
      className,
    },
    ref
  ) => {
    const isControlled = value !== undefined;
    const [internalTags, setInternalTags] = React.useState<string[]>(defaultValue ?? []);
    const [inputValue, setInputValue] = React.useState('');
    const [isFocused, setIsFocused] = React.useState(false);
    const [enteringIdx, setEnteringIdx] = React.useState<number | null>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const tags = isControlled ? value ?? [] : internalTags;

    const updateTags = (next: string[]) => {
      if (!isControlled) setInternalTags(next);
      onChange?.(next);
    };

    const addTag = (val: string) => {
      const trimmed = val.trim();
      if (!trimmed || tags.includes(trimmed)) return;
      const next = [...tags, trimmed];
      setEnteringIdx(next.length - 1);
      updateTags(next);
      setInputValue('');
    };

    const removeTag = (idx: number) => {
      const next = tags.filter((_, i) => i !== idx);
      updateTags(next);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (addOnEnter && e.key === 'Enter') {
        e.preventDefault();
        addTag(inputValue);
      } else if (e.key === 'Backspace' && inputValue === '') {
        removeTag(tags.length - 1);
      }
    };

    const handleShellClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).closest('button')) return;
      inputRef.current?.focus();
    };

    // Clear entering animation class on next render
    React.useEffect(() => {
      if (enteringIdx === null) return;
      const id = requestAnimationFrame(() => setEnteringIdx(null));
      return () => cancelAnimationFrame(id);
    }, [enteringIdx]);

    return (
      <div
        ref={ref}
        className={cx(
          'ds-tag-input',
          `ds-tag-input--${size}`,
          {
            'ds-tag-input--focused': isFocused,
            'ds-tag-input--disabled': disabled,
            'ds-tag-input--invalid': invalid,
          },
          className
        )}
        onClick={handleShellClick}>
        {icon && <Icon name={icon} size={size} className="ds-tag-input__icon" aria-hidden />}
        {tags.map((tag, idx) => (
          <span
            key={tag}
            className={cx('ds-tag-input__pill', {
              'ds-tag-input__pill--entering': idx === enteringIdx,
            })}>
            <span className="ds-tag-input__pill-label">{tag}</span>
            <button
              type="button"
              className="ds-tag-input__pill-remove"
              aria-label={`Remove ${tag}`}
              disabled={disabled}
              onClick={() => removeTag(idx)}>
              <Icon name="actionable/close" size="small" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          name={name}
          aria-label={ariaLabel}
          className="ds-tag-input__input"
          type="text"
          value={inputValue}
          disabled={disabled}
          placeholder={tags.length === 0 ? placeholder : undefined}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
      </div>
    );
  }
);

TagInput.displayName = 'TagInput';

export default TagInput;
